import { useEffect, useState, type RefObject } from 'react'

/** Below this height gap, the two columns are ~even and there's no whitespace
 *  worth closing — don't stick (avoids churn on near-equal layouts). */
const MIN_STICK_DELTA_PX = 40

/**
 * Decide which of two side-by-side columns should be `position: sticky`.
 *
 * Rule: stick the SHORTER column. If the shorter column scrolls, it leaves a
 * band of dead whitespace beside the taller one; pinning it closes that gap
 * and keeps its top content (the buy box in the sidebar, or the product image
 * in the gallery) on screen through the taller column's scroll.
 *
 * We do NOT refuse to stick a column that's taller than the viewport. That
 * guard used to leave the exact whitespace this feature removes: on rich PDPs
 * the shorter column often slightly exceeds the viewport, and refusing meant
 * "no sticky, keep the gap" (observed on the golden LG page). A top-heavy
 * sticky column overflowing the viewport is fine — its key content (price/CTA,
 * or the hero image) sits at the top and stays pinned; the overflow tail
 * simply reveals as the sticky range releases past the hero. The only case
 * with nothing to gain is near-equal heights → null.
 *
 * Pure + deterministic so it can be unit-tested without a DOM.
 */
export function pickStickyColumn(leftH: number, rightH: number): 'left' | 'right' | null {
  if (Math.abs(leftH - rightH) < MIN_STICK_DELTA_PX) return null
  return leftH < rightH ? 'left' : 'right'
}

/**
 * Reactively pick the sticky hero column. Desktop-only (>= lg / 1024px);
 * returns `null` below that so the mobile single-column flow is untouched.
 *
 * Decides on the COLLAPSED baseline at mount, and re-decides on viewport
 * resize, lg-breakpoint crossing, and the `ready` transition (late-mount data
 * load). It deliberately does NOT re-decide on content growth (offers expand /
 * "read more" / "show all"): un-sticking a column that grew past the fit guard
 * mid-interaction would yank the just-expanded content off-screen. Content
 * amount is managed by those collapse/clamp toggles keeping the baseline
 * compact — not by re-flowing the sticky column under the user.
 *
 * SSR-safe: starts `null` (no sticky class until mount). Since `position:
 * sticky` doesn't move an element in normal flow, adding the class post-mount
 * causes no layout shift and no hydration mismatch.
 */
export function useStickyColumn(
  leftRef: RefObject<HTMLElement | null>,
  rightRef: RefObject<HTMLElement | null>,
  // Re-attach trigger: the refs' target elements only exist once the hero has
  // rendered. On the SSR path they're present at mount, but on the client-only
  // fallback (initialData === null) they mount LATER — pass a value that flips
  // when the hero becomes available so the effect re-runs and measures then.
  // Without this the effect (stable ref deps) runs once against absent columns
  // and the sticky decision stays permanently inert on that path.
  ready: boolean = true
): 'left' | 'right' | null {
  const [side, setSide] = useState<'left' | 'right' | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const desktop = window.matchMedia('(min-width: 1024px)')

    const recompute = () => {
      const left = leftRef.current
      const right = rightRef.current
      if (!desktop.matches || !left || !right) {
        setSide(null)
        return
      }
      // Measure the natural (non-sticky) heights. offsetHeight is unaffected
      // by a sticky child, so re-measuring while one side is already sticky
      // stays stable (no oscillation).
      setSide(pickStickyColumn(left.offsetHeight, right.offsetHeight))
    }

    // Decide on: mount, viewport resize, lg-breakpoint crossing, and the
    // `ready` transition (late-mount data load). Deliberately NOT on content
    // growth: re-deciding when a user expands the sidebar "Read more" or specs
    // "Show all" would flip the sticky column mid-interaction, yanking the
    // just-expanded content off-screen. The decision is made on the COLLAPSED
    // baseline (description clamp + specs collapse keep it compact); content
    // amount is handled by those toggles, not by re-flowing the sticky column.
    window.addEventListener('resize', recompute, { passive: true })
    desktop.addEventListener('change', recompute)
    recompute()

    return () => {
      window.removeEventListener('resize', recompute)
      desktop.removeEventListener('change', recompute)
    }
  }, [leftRef, rightRef, ready])

  return side
}
